/**
 * Notification Template Library — Module 4 UI.
 *
 * Renders the three HIPAA breach notification templates as selectable cards.
 * When the user clicks "Generate", a modal collects merge-field values (org
 * name, contact info, privacy officer, steps-to-protect, etc.) and then
 * produces a Word document populated with the incident data plus the merged
 * fields. Anything the user leaves blank stays as a [PLACEHOLDER: ...] in
 * the downloaded doc, so they can still edit it in Word after download.
 *
 * The cards also surface which incident fields WILL be used (a preview) so
 * the user can see which §164.404(c) required elements are present vs missing
 * before they generate.
 */

import React, { useState } from 'react';
import { TEMPLATE_CATALOG, downloadTemplate } from './notificationTemplates';
import { STATE_NOTICE_MODULES, STATE_MODULES_VERIFIED_AS_OF } from './noticeContent';

// ---------------------------------------------------------------------------
// Required-content checklist for the preview (§164.404(c))
// ---------------------------------------------------------------------------
function contentChecklist(incident) {
  return [
    {
      key: 'what_happened',
      label: 'What happened',
      citation: '§164.404(c)(1)(A)',
      satisfied: !!(incident.facts || incident.summary),
      source: incident.facts ? 'incident.facts' : incident.summary ? 'incident.summary' : null
    },
    {
      key: 'dates',
      label: 'Dates of breach and discovery',
      citation: '§164.404(c)(1)(A)',
      satisfied: !!incident.discovery_date,
      source: incident.discovery_date ? 'incident.discovery_date' : null
    },
    {
      key: 'phi_types',
      label: 'Types of PHI involved',
      citation: '§164.404(c)(1)(B)',
      satisfied: !!incident.type_of_phi,
      source: incident.type_of_phi ? 'incident.type_of_phi' : null
    },
    {
      key: 'mitigation',
      label: 'What the entity is doing',
      citation: '§164.404(c)(1)(D)',
      satisfied: !!(incident.actions_taken || incident.safeguards_in_place),
      source: incident.actions_taken ? 'incident.actions_taken' : incident.safeguards_in_place ? 'incident.safeguards_in_place' : null
    },
    {
      key: 'steps_to_protect',
      label: 'Steps individuals should take',
      citation: '§164.404(c)(1)(C)',
      satisfied: true, // the standard protections block is included automatically
      source: 'standard protections block (credit bureaus, fraud alerts, freezes, FTC, Medicare guidance) — auto-included'
    },
    {
      key: 'contact',
      label: 'Contact procedures',
      citation: '§164.404(c)(1)(E)',
      satisfied: false, // always user-supplied
      source: 'merge field',
      userSupplied: true
    }
  ];
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------
function Icon({ name, className }) {
  const cls = className || 'w-6 h-6';
  if (name === 'user') {
    return (
      <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    );
  }
  if (name === 'megaphone') {
    return (
      <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
      </svg>
    );
  }
  if (name === 'globe') {
    return (
      <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
      </svg>
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function NotificationTemplateLibrary({ incident }) {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [mergeValues, setMergeValues] = useState({});
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [lastDownloaded, setLastDownloaded] = useState(null);

  const checklist = contentChecklist(incident);
  const satisfiedCount = checklist.filter(c => c.satisfied).length;
  const userSuppliedCount = checklist.filter(c => c.userSupplied).length;
  const totalRequired = checklist.length;

  // The case's affected states drive the state-content section of the
  // individual letter. Default: all mapped states selected.
  const affectedStates = (incident.affected_states || []).map((s) => ({
    code: String(s.state_code || '').toUpperCase(),
    name: s.state_name || s.state_code,
  })).filter((s) => s.code);

  const openTemplate = (template) => {
    setSelectedTemplate(template);
    setMergeValues(template.id === 'individual' ? { stateCodes: affectedStates.map((s) => s.code) } : {});
    setError('');
  };

  const toggleState = (code) => {
    setMergeValues((v) => {
      const cur = new Set(v.stateCodes || []);
      if (cur.has(code)) cur.delete(code); else cur.add(code);
      return { ...v, stateCodes: [...cur] };
    });
  };

  const closeTemplate = () => {
    setSelectedTemplate(null);
    setMergeValues({});
    setError('');
  };

  // Which required merge fields are still blank? Used to gate the Generate button.
  const missingRequired = selectedTemplate
    ? selectedTemplate.mergeFields.filter(
        (f) => f.required && !(mergeValues[f.key] && String(mergeValues[f.key]).trim())
      )
    : [];

  const handleGenerate = async () => {
    if (!selectedTemplate) return;
    if (missingRequired.length > 0) {
      setError(`Please fill in: ${missingRequired.map((f) => f.label).join(', ')}`);
      return;
    }
    setGenerating(true);
    setError('');
    try {
      const name = await downloadTemplate(selectedTemplate.id, incident, mergeValues);
      setLastDownloaded(name);
      closeTemplate();
    } catch (err) {
      setError(err.message || 'Failed to generate template');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header + readiness summary */}
      <div className="bg-gradient-to-br from-indigo-50 via-white to-blue-50 border border-indigo-100 rounded-3xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white shadow-md shadow-indigo-500/25">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Notification template library</h3>
                <p className="text-xs text-slate-500">45 CFR §164.404 — individual, media, and substitute notices</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 mt-2">
              Generate HIPAA-compliant breach notification letters pre-populated from this incident&rsquo;s data. Fill in organization-level details (contact info, privacy officer) before downloading.
            </p>
          </div>
          <div className="flex-shrink-0 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 min-w-[180px]">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Content readiness</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{satisfiedCount} of {totalRequired - userSuppliedCount}</p>
            <p className="text-[11px] text-slate-500 mt-0.5">auto-filled from incident · {userSuppliedCount} always user-supplied</p>
          </div>
        </div>
      </div>

      {/* §164.404(c) content checklist */}
      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
        <h4 className="text-sm font-semibold text-slate-900 mb-1">§164.404(c) required content</h4>
        <p className="text-xs text-slate-500 mb-4">What the generated letter must contain. Items with a check will auto-fill from this incident.</p>
        <ul className="space-y-2">
          {checklist.map((item) => (
            <li key={item.key} className="flex items-center gap-3">
              <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                item.satisfied ? 'bg-emerald-500 text-white' :
                item.userSupplied ? 'bg-indigo-100 text-indigo-600' :
                'bg-amber-100 text-amber-600'
              }`}>
                {item.satisfied ? (
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : item.userSupplied ? (
                  <span className="text-[9px] font-bold">U</span>
                ) : (
                  <span className="text-[9px] font-bold">!</span>
                )}
              </div>
              <div className="flex-1 flex items-baseline justify-between gap-2 min-w-0">
                <span className="text-sm text-slate-700">{item.label}</span>
                <span className="text-[10px] text-slate-400 font-mono">{item.citation}</span>
              </div>
              {!item.satisfied && !item.userSupplied && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium">
                  placeholder
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Template cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {TEMPLATE_CATALOG.map((template) => (
          <button
            key={template.id}
            type="button"
            onClick={() => openTemplate(template)}
            className="group text-left bg-white rounded-3xl border border-slate-200 shadow-sm hover:shadow-xl hover:border-indigo-300 hover:-translate-y-0.5 transition-all overflow-hidden"
          >
            <div className="p-6">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-100 to-blue-100 text-indigo-600 flex items-center justify-center mb-4 group-hover:from-indigo-500 group-hover:to-blue-600 group-hover:text-white transition-all">
                <Icon name={template.icon} />
              </div>
              <h4 className="font-bold text-slate-900 mb-1">{template.title}</h4>
              <p className="text-[11px] text-slate-500 font-mono mb-3">{template.subtitle}</p>
              <p className="text-sm text-slate-600 leading-relaxed">{template.description}</p>
            </div>
            <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
              <span className="text-xs text-slate-500">{template.mergeFields.length} merge fields</span>
              <span className="text-sm font-semibold text-indigo-600 group-hover:text-indigo-700">Generate →</span>
            </div>
          </button>
        ))}
      </div>

      {lastDownloaded && (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800">
          ✓ Downloaded <code className="font-mono text-xs">{lastDownloaded}</code>. Open in Word and review before sending.
        </div>
      )}

      {/* Merge-field modal */}
      {selectedTemplate && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={closeTemplate}>
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-slate-200 flex items-start justify-between gap-4">
              <div>
                <h3 className="font-bold text-slate-900">{selectedTemplate.title}</h3>
                <p className="text-xs text-slate-500 font-mono mt-0.5">{selectedTemplate.subtitle}</p>
              </div>
              <button
                type="button"
                onClick={closeTemplate}
                className="text-slate-400 hover:text-slate-600 text-2xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-auto px-6 py-5 space-y-4">
              <p className="text-xs text-slate-500">
                Fill in the merge fields below. Anything left blank will remain as a <code className="font-mono text-slate-600">[PLACEHOLDER]</code> in the downloaded doc for you to edit in Word.
              </p>

              {selectedTemplate.id === 'individual' && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">State-specific content</p>
                  <p className="text-xs text-slate-500 mb-3">
                    The letter automatically adds each selected state's statutorily required content (verified as of {STATE_MODULES_VERIFIED_AS_OF}). A selected state without a verified module gets a loud review-required box in the letter instead of a silent gap.
                  </p>
                  {affectedStates.length === 0 && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      No affected states are mapped on this case yet. Map them in the Jurisdictions tab so residents get their state's required content.
                    </p>
                  )}
                  <div className="space-y-2">
                    {affectedStates.map((s) => {
                      const mod = STATE_NOTICE_MODULES[s.code];
                      const on = (mergeValues.stateCodes || []).includes(s.code);
                      return (
                        <div key={s.code} className="flex items-start gap-2">
                          <input type="checkbox" checked={on} onChange={() => toggleState(s.code)} className="mt-0.5" />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-slate-800">{s.name} ({s.code})</span>
                              {mod ? (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">verified · {mod.citation}</span>
                              ) : (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">no verified module — review box added to letter</span>
                              )}
                            </div>
                            {mod && (mod.reviewNotes || []).map((n, i) => (
                              <p key={i} className="text-[11px] text-slate-500 mt-1">{n}</p>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {selectedTemplate.mergeFields.map((field) => (
                <div key={field.key}>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {field.multiline ? (
                    <textarea
                      value={mergeValues[field.key] || ''}
                      onChange={(e) => setMergeValues({ ...mergeValues, [field.key]: e.target.value })}
                      placeholder={field.placeholder}
                      rows={3}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-900 bg-white shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    />
                  ) : (
                    <input
                      type="text"
                      value={mergeValues[field.key] || ''}
                      onChange={(e) => setMergeValues({ ...mergeValues, [field.key]: e.target.value })}
                      placeholder={field.placeholder}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-900 bg-white shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    />
                  )}
                </div>
              ))}
            </div>

            {error && (
              <div className="mx-6 mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeTemplate}
                className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium hover:bg-white transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleGenerate}
                disabled={generating || missingRequired.length > 0}
                title={missingRequired.length > 0 ? `Fill in: ${missingRequired.map((f) => f.label).join(', ')}` : ''}
                className="px-5 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-sm font-semibold shadow-md hover:shadow-lg hover:shadow-indigo-600/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none transition-all"
              >
                {generating ? 'Generating...' : missingRequired.length > 0 ? `Fill ${missingRequired.length} required field${missingRequired.length === 1 ? '' : 's'}` : 'Generate & Download'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
